import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import {
  useCreateModuleParameterSchemaMutation,
  useDeleteModuleParameterSchemaMutation,
  useGetModuleParameterSchemasQuery,
  useUpdateModuleParameterSchemaMutation
} from 'data/generated/graphql';
import type { ModuleParameterSchemaEntry } from './ModuleParameterSettings';

export const useModuleParameterSchemas = (publicationKey: string) => {
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useGetModuleParameterSchemasQuery(
    { publicationKey },
    { enabled: !!publicationKey }
  );
  const schemas: ModuleParameterSchemaEntry[] = (data?.getModuleParameterSchemas ?? []).map((s) => ({
    key: s.key,
    label: s.label,
    type: s.type,
    enumValues: s.enumValues ?? undefined,
    deprecated: s.deprecated === true ? true : undefined
  }));

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries(['GetModuleParameterSchemas', { publicationKey }]);
  }, [queryClient, publicationKey]);

  const createMutation = useCreateModuleParameterSchemaMutation();
  const updateMutation = useUpdateModuleParameterSchemaMutation();
  const deleteMutation = useDeleteModuleParameterSchemaMutation();

  const createSchema = useCallback(
    (entry: ModuleParameterSchemaEntry) =>
      new Promise<void>((resolve, reject) => {
        createMutation.mutate(
          {
            publicationKey,
            input: {
              key: entry.key,
              label: entry.label,
              type: entry.type,
              enumValues: entry.enumValues ?? null,
              deprecated: entry.deprecated ?? null
            }
          },
          {
            onSuccess: () => {
              invalidate();
              resolve();
            },
            onError: reject
          }
        );
      }),
    [createMutation, publicationKey, invalidate]
  );

  const updateSchema = useCallback(
    (key: string, entry: ModuleParameterSchemaEntry) =>
      new Promise<void>((resolve, reject) => {
        updateMutation.mutate(
          {
            publicationKey,
            key,
            input: {
              key: entry.key,
              label: entry.label,
              type: entry.type,
              enumValues: entry.enumValues ?? null,
              deprecated: entry.deprecated ?? null
            }
          },
          {
            onSuccess: () => {
              invalidate();
              resolve();
            },
            onError: reject
          }
        );
      }),
    [updateMutation, publicationKey, invalidate]
  );

  const deleteSchema = useCallback(
    (key: string) =>
      new Promise<void>((resolve, reject) => {
        deleteMutation.mutate(
          { publicationKey, key },
          {
            onSuccess: () => {
              invalidate();
              resolve();
            },
            onError: reject
          }
        );
      }),
    [deleteMutation, publicationKey, invalidate]
  );

  return {
    schemas,
    isLoading,
    error,
    createSchema,
    updateSchema,
    deleteSchema
  };
};
